import { FormLabel, Stack, Switch, TextField } from "@mui/material";
import { ReactNode } from "react";
import { WpBoolean } from "@hooks/use-config/types";
import { WpTaxonomyValue } from "@hooks/use-get/taxonomy-values";
import { TableCellInputType } from "@components/data-table/types";
import { OutlinedSelect, OutlinedInput } from "../../../components";
import { OutlinedDatePicker } from "../../../components/date-picker";
import { MultipleFileUploader } from "@components/multiple-file-uploader";
import { UserAutocomplete } from "@components/user-autocomplete";
import { AssetAutocomplete } from "@views/tickets-table/add-ticket-modal/asset-autocomplete";
import { TICKET_KEY_FOR_ASSETS } from "@utils/types";

const inputComponents: Partial<Record<TableCellInputType, any>> = {
  [TableCellInputType.Text]: OutlinedInput,
  [TableCellInputType.Select]: OutlinedSelect,
  [TableCellInputType.MultiLine]: OutlinedInput,
  [TableCellInputType.Boolean]: Switch,
  [TableCellInputType.Date]: OutlinedDatePicker,
  [TableCellInputType.DateTime]: OutlinedDatePicker,
  [TableCellInputType.FileUpload]: MultipleFileUploader,
};

export const AddAssetField = ({
  inputType,
  name,
  label,
  options,
  onChange,
  value,
  files,
  addFiles,
  removeFile,
  transferNotes,
  onTransferNotesChange,
  showTransferNotes,
  helperText,
  disabled,
}: {
  inputType: TableCellInputType;
  name: string;
  label: string;
  options?: WpTaxonomyValue[];
  onChange: (value: string) => void;
  value: string | number | boolean;
  files?: File[];
  addFiles?: (files: File[]) => void;
  removeFile?: (fileName: string) => void;
  transferNotes?: string;
  onTransferNotesChange?: (val: string) => void;
  showTransferNotes?: boolean;
  helperText?: ReactNode;
  disabled?: boolean;
}) => {
  const InputComponent = inputComponents[inputType] || OutlinedInput;
  const handleChange = (value: any) => {
    onChange(value as string);
  };

  if (inputType === TableCellInputType.UserInput) {
    return (
      <Stack direction="column" spacing={2}>
        <UserAutocomplete
          label={label}
          value={value as number | string | null}
          onChange={(id) => onChange(id !== null ? String(id) : "")}
          disabled={disabled}
        />
        {showTransferNotes && (
          <TextField
            label="Transfer Notes"
            placeholder="Optional notes for this transfer..."
            value={transferNotes ?? ""}
            onChange={(e) => onTransferNotesChange?.(e.target.value)}
            multiline
            rows={3}
            fullWidth
            size="small"
            disabled={disabled}
          />
        )}
      </Stack>
    );
  }

  return (
    <>
      {inputType === TableCellInputType.Boolean && (
        <FormLabel>{label}</FormLabel>
      )}
      {name === TICKET_KEY_FOR_ASSETS ? (
        <AssetAutocomplete
          value={value as any}
          onChange={(ids) => handleChange(ids?.[0]?.toString() ?? "")}
          label={label}
          disabled={disabled}
        />
      ) : (
        <>
          {inputType === TableCellInputType.Boolean && (
            <InputComponent
              checked={value === WpBoolean.YES}
              onChange={() =>
                handleChange(value === WpBoolean.YES ? WpBoolean.NO : WpBoolean.YES)
              }
              disabled={disabled}
            />
          )}

          {inputType === TableCellInputType.FileUpload && (
            <InputComponent
              name={name}
              value={value}
              onChange={(nextValue: any) => handleChange(nextValue)}
              files={files}
              addFiles={addFiles}
              removeFile={removeFile}
            />
          )}

          {inputType !== TableCellInputType.Boolean &&
            inputType !== TableCellInputType.FileUpload && (
              <InputComponent
                name={name}
                label={label}
                value={value}
                onChange={(nextValue: any) => handleChange(nextValue)}
                options={options}
                multiline={
                  inputType === TableCellInputType.MultiLine ? true : undefined
                }
                helperText={helperText}
                disabled={disabled}
              />
            )}
        </>
      )}
    </>
  );
};

export default AddAssetField;
